/**
 * Back-in-stock notification — Klaviyo modal variant.
 *
 * - Shows a "Notify when available" trigger button when the selected variant
 *   is sold out, replacing the native add-to-cart button.
 * - Clicking the trigger opens a <dialog> with an email-capture form.
 * - On submit, POSTs to Klaviyo's Back in Stock Subscriptions API using the
 *   store's Public API key (safe to expose client-side).
 * - On variant:update, swaps visibility with the native ATC wrapper and keeps
 *   the current variant ID in sync.
 */
class BackInStockNotification extends HTMLElement {
  connectedCallback() {
    this._atcWrapper = this.parentElement?.querySelector('[data-ref="atc-wrapper"]');
    this._productDetails = this.closest('.product-details');
    this._trigger = this.querySelector('[data-ref="trigger"]');
    this._dialog = this.querySelector('[data-ref="dialog"]');
    this._closeBtn = this.querySelector('[data-ref="close"]');
    this._form = this.querySelector('[data-ref="form"]');
    this._emailInput = this.querySelector('[data-ref="email-input"]');
    this._formWrapper = this.querySelector('[data-ref="form-wrapper"]');
    this._successWrapper = this.querySelector('[data-ref="success-wrapper"]');
    this._submitBtn = this.querySelector('[data-ref="submit-btn"]');

    this._trigger?.addEventListener('click', () => this._openDialog());
    this._closeBtn?.addEventListener('click', () => this._dialog?.close());

    // Close on backdrop click.
    this._dialog?.addEventListener('click', (e) => {
      if (e.target === this._dialog) this._dialog.close();
    });

    this._form?.addEventListener('submit', this._onSubmit.bind(this));

    this._boundVariantUpdate = this._onVariantUpdate.bind(this);
    document.addEventListener('variant:update', this._boundVariantUpdate);

    this._syncSoldOutState();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this._boundVariantUpdate);
  }

  _openDialog() {
    this._resetToForm();
    this._dialog?.showModal();
  }

  _onVariantUpdate(event) {
    const { resource, data } = event.detail;
    if (!resource || String(data.productId) !== String(this.dataset.productId)) return;

    // Track the currently selected variant for the Klaviyo submission.
    this.dataset.variantId = resource.id;

    if (resource.available) {
      this.setAttribute('hidden', '');
      this._atcWrapper?.removeAttribute('hidden');
      this._dialog?.close();
    } else {
      this._atcWrapper?.setAttribute('hidden', '');
      this.removeAttribute('hidden');
    }

    this._syncSoldOutState();
  }

  _syncSoldOutState() {
    this._productDetails?.classList.toggle('product-details--sold-out-notify-active', !this.hasAttribute('hidden'));
  }

  _resetToForm() {
    if (this._formWrapper) this._formWrapper.hidden = false;
    if (this._successWrapper) this._successWrapper.hidden = true;
    if (this._form) this._form.reset();
    if (this._submitBtn) {
      this._submitBtn.disabled = false;
      this._submitBtn.removeAttribute('aria-busy');
    }
  }

  async _onSubmit(event) {
    event.preventDefault();

    const email = this._emailInput?.value?.trim();
    const variantId = this.dataset.variantId;
    const klaviyoKey = this.dataset.klaviyoKey;

    if (!klaviyoKey) {
      console.error('[Back in Stock] Klaviyo Public API key is not set. Go to Theme Settings → Integrations → Klaviyo and add your Public API key.');
      return;
    }

    if (!email || !variantId) return;

    this._submitBtn.disabled = true;
    this._submitBtn.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2023-12-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      // Klaviyo returns 202 Accepted on success.
      if (response.status === 202) {
        if (this._formWrapper) this._formWrapper.hidden = true;
        if (this._successWrapper) this._successWrapper.hidden = false;
      } else {
        this._submitBtn.disabled = false;
        this._submitBtn.removeAttribute('aria-busy');
      }
    } catch {
      this._submitBtn.disabled = false;
      this._submitBtn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('back-in-stock-notification')) {
  customElements.define('back-in-stock-notification', BackInStockNotification);
}
